import './Contacts.css';
import React, { useState, useEffect } from 'react';
import { fetchAssetByID } from '../../services/clientService';
import { read, utils } from 'xlsx-js-style';

function IngagedContactsTable({
    containerAssets,
    title,
    isMobileOverride = null,
}) {
    const [contactsTableColumns, setContactsTableColumns] = useState([]);
    const [contactsTableBody, setContactsTableBody] = useState([]);
    const [contactsTableData, setContactsTableData] = useState([]);

    const emailRegex = /\S+@\S+\.\S+/;
    const phoneRegex =
        /^[\+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/;

    const isEmail = value =>
        typeof value === 'string' && emailRegex.test(value);

    const isPhone = value => {
        if (typeof value !== 'string') return false;
        const cleaned = value.replace(/[\s\-\.\(\)]/g, '');
        return (
            phoneRegex.test(value) &&
            cleaned.length >= 10 &&
            cleaned.length <= 15
        );
    };

    const renderCell = value => {
        if (value == null || value === '') return '';

        if (isEmail(value)) {
            return (
                <a
                    href={`mailto:${value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0066cc' }}
                >
                    {value}
                </a>
            );
        }

        if (isPhone(value)) {
            const telLink = value.replace(/[^\d+]/g, '');
            return (
                <a
                    href={`tel:${telLink}`}
                    style={{ color: '#0066cc', cursor: 'pointer' }}
                >
                    {value}
                </a>
            );
        }

        return value;
    };

    const dateRegex = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;

    const formatDate = date => {
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const yy = String(date.getFullYear()).slice(-2);
        return `${mm}/${dd}/${yy}`;
    };

    const parseDateString = str => {
        const [mStr, dStr, yStr] = str.split('/');
        const m = parseInt(mStr, 10);
        const d = parseInt(dStr, 10);
        let y = parseInt(yStr, 10);
        if (yStr.length === 2) {
            y += y < 70 ? 2000 : 1900;
        }
        const dateObj = new Date(y, m - 1, d);
        return isNaN(dateObj.getTime()) ? null : dateObj;
    };

    const parseAndFormatDate = cell => {
        if (cell instanceof Date) return formatDate(cell);
        if (typeof cell === 'string' && dateRegex.test(cell)) {
            const parsed = parseDateString(cell);
            if (parsed) return formatDate(parsed);
        }
        return cell;
    };

    const fetchAssets = async () => {
        if (!Array.isArray(containerAssets)) return;

        for (const containerAsset of containerAssets) {
            const fetchedAsset = await fetchAssetByID(
                containerAsset.assetId,
                null,
            );
            const response = await fetch(fetchedAsset.url);
            const data = await response.arrayBuffer();
            const workbook = read(data, { type: 'array', cellDates: true });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const tableData = utils.sheet_to_json(worksheet, { header: 1 });
            setContactsTableData(tableData);
        }
    };

    useEffect(() => {
        fetchAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerAssets]);

    useEffect(() => {
        if (
            contactsTableData &&
            Array.isArray(contactsTableData) &&
            contactsTableData.length > 0
        ) {
            const newColumns = contactsTableData[0];
            const newBody = contactsTableData.slice(1).map(row => {
                const rowData = {};
                newColumns.forEach((column, idx) => {
                    let cell = row[idx];
                    cell = parseAndFormatDate(cell);
                    rowData[column] = cell;
                });
                return rowData;
            });
            setContactsTableColumns(newColumns);
            setContactsTableBody(newBody);
        }
    }, [contactsTableData]);

    const [isMobileWindow, setIsMobileWindow] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 786 : false,
    );
    const isMobile =
        isMobileOverride !== null ? isMobileOverride : isMobileWindow;
    const [expanded, setExpanded] = useState({});

    const toggleExpand = idx => {
        setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    useEffect(() => {
        if (contactsTableBody.length > 0) {
            const initialExpanded = {};
            contactsTableBody.forEach((_, idx) => {
                initialExpanded[idx] = true;
            });
            setExpanded(initialExpanded);
        }
    }, [contactsTableBody]);

    useEffect(() => {
        const handler = () => setIsMobileWindow(window.innerWidth <= 786);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    return (
        <>
            {isMobile ? (
                <div className="safety-contacts">
                    <h2 className="ing-contact-title">{title}</h2>
                    {contactsTableBody.map((row, idx) => {
                        const [firstCol, secondCol, ...restCols] =
                            contactsTableColumns;
                        const titleVal = row[firstCol] ?? '';
                        const trivialVal = row[secondCol] ?? '';
                        const extraDetails = restCols
                            .map(col => ({ label: col, value: row[col] }))
                            .filter(d => d.value != null && d.value !== '');

                        return (
                            <div
                                key={idx}
                                className="contact-card"
                                style={expanded[idx] ? { height: 'auto' } : {}}
                            >
                                <div className="contact-info">
                                    <div className="contact-title">
                                        {titleVal}
                                    </div>
                                    {extraDetails.length > 0 && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '18px',
                                                right: '10px',
                                            }}
                                        >
                                            {expanded[idx] ? (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 18 17"
                                                    fill="none"
                                                    style={{
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() =>
                                                        toggleExpand(idx)
                                                    }
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M8.40784 5.58981C8.60905 5.39454 8.93528 5.39454 9.13648 5.58981L15.3192 11.5898C15.5204 11.7851 15.5204 12.1017 15.3192 12.2969C15.118 12.4922 14.7918 12.4922 14.5906 12.2969L8.77216 6.65047L2.95377 12.2969C2.75256 12.4922 2.42633 12.4922 2.22513 12.2969C2.02392 12.1017 2.02392 11.7851 2.22513 11.5898L8.40784 5.58981Z"
                                                        fill="black"
                                                    />
                                                </svg>
                                            ) : (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 17 17"
                                                    fill="none"
                                                    style={{
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() =>
                                                        toggleExpand(idx)
                                                    }
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M9.09407 11.9273C8.89792 12.1225 8.57886 12.1225 8.38143 11.9273L2.31489 5.92745C2.11747 5.7322 2.11643 5.41562 2.31258 5.22037C2.50873 5.02511 2.82779 5.02511 3.02522 5.22037L8.73428 10.8667L14.4064 5.22037C14.6026 5.02511 14.9216 5.02511 15.1191 5.22037C15.3165 5.41562 15.3175 5.7322 15.1214 5.92745L9.09407 11.9273Z"
                                                        fill="black"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="contact-extra">
                                    {expanded[idx] && (
                                        <>
                                            <div
                                                className="contact-extra-row"
                                                style={{ paddingLeft: '0px' }}
                                            >
                                                <div className="ing-email-label">
                                                    {secondCol}:
                                                </div>
                                                <div className="ing-email-dynamicLbl">
                                                    {renderCell(trivialVal)}
                                                </div>
                                            </div>
                                            {extraDetails.length > 0 && (
                                                <>
                                                    {extraDetails.map(
                                                        (detail, dIdx) => (
                                                            <div
                                                                key={dIdx}
                                                                className="contact-extra-row"
                                                            >
                                                                <div className="ing-email-label">
                                                                    {
                                                                        detail.label
                                                                    }
                                                                    :
                                                                </div>
                                                                <div className="ing-email-dynamicLbl">
                                                                    {renderCell(
                                                                        detail.value,
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <table className="ingaged-bold-dark1 table ingaged-table">
                    <thead className="ingaged-bold-dark1">
                        <tr>
                            {contactsTableColumns.map((hdr, i) => (
                                <th
                                    key={i}
                                    className="ingaged-bold-dark1 no-bottom-border"
                                >
                                    {hdr}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {contactsTableBody.map((row, rIdx) => (
                            <tr key={rIdx}>
                                {contactsTableColumns.map((col, cIdx) => (
                                    <td
                                        key={cIdx}
                                        className={
                                            cIdx === 0
                                                ? 'ingaged-bold-blue'
                                                : 'ingaged-body-cells'
                                        }
                                    >
                                        {renderCell(row[col])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </>
    );
}
export default IngagedContactsTable;
